import React, { useEffect, useState } from "react";
import { loginByEmail, restorePassword } from '../../utils/api';
import InputField from "./InputField";
import UIButton from "../../../../globalComponents/UIComponents/UIButton";

const passwordLockTimeoutFormat = (timeout) => {
    let minutes = Math.floor(timeout / 60);
    let seconds = timeout % 60;
    return [
        minutes,
        seconds < 10 ? '0' + seconds : seconds,
    ].join(':');
}

export const PasswordLock = ({ passwordLockTime, onStop }) => {
    const [passwordUnlockTimeout, setPasswordUnloadTimeout] = useState(null);

    useEffect(() => {
        let timer;

        const updateTimer = () => {
            const timeout = Math.ceil(passwordLockTime - (Date.now() / 1000));
            if (timeout > 0) {
                setPasswordUnloadTimeout(timeout);
                timer = setTimeout(updateTimer, 1000);
            } else {
                setPasswordUnloadTimeout(null);
                if (onStop) {
                    onStop();
                }
            }
        }

        updateTimer();

        return () => {
            clearTimeout(timer);
        }
    }, [passwordLockTime]);

    return (
        <span>{passwordLockTimeoutFormat(passwordUnlockTimeout)}</span>
    );
}

export default ({
    preview,
    $locale,
    eventKey,
    email,
    handleSendSMS = () => {},
    phoneAuthAllowed,
    passwordUpdateLock,
    onSuccess,
    onReset,
}) => {
    const [password, setPassword] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [passwordLockTime, setPasswordLockTime] = useState(null);
    const [error, setError] = useState({});
    const [isPasswordVisible, setPasswordVisible] = useState(false);

    useEffect(() => {
        setPasswordLockTime(passwordUpdateLock);
    }, [passwordUpdateLock]);

    const handleSetError = (value) => {
        if (value) return;

        setError({ password: { hasError: true, name: '' } });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (disabled || preview) {
            return;
        }

        setDisabled(true);
        try {
            if (onSuccess) {
                const client = await loginByEmail(eventKey, { email, code: password });
                await onSuccess(client);
            }
        } catch (e) {
            setError({
                password: {
                    hasError: true,
                    name: password.length ? $locale.get('auth.login.email.wrongCode') : '',
                },
            });
            setDisabled(false);
        }
    }

    const handleResetPassword = async () => {
        if (preview) return;
        const { passwordUpdateLock } = await restorePassword(eventKey, { email });
        setPasswordLockTime(passwordUpdateLock);
    }

    return (
        <form
            onSubmit={handleSubmit}
            className="email-request-form auth-container"
            noValidate
        >
            <div>
                <InputField
                    label={$locale.get('auth.login.email.email')}
                    type="email"
                    name="email"
                    value={email}
                    disabled={true}
                    readOnly={true}
                    required={true}
                    $locale={$locale}
                />
                {
                    onReset &&
                    <UIButton
                        type="button"
                        color="transparent"
                        style="link"
                        onClick={() => onReset()}
                    >
                        {$locale.get('auth.login.button.changeEmail')}
                    </UIButton>
                }
            </div>
            <div>
                <InputField
                    value={password}
                    label={$locale.get('auth.login.email.onetimeCode')}
                    type={isPasswordVisible ? 'text' : 'password'}
                    name="password"
                    autoComplete="new-password"
                    required
                    placeholder={$locale.get('auth.login.email.success.password.placeholder')}
                    disabled={disabled}
                    error={error?.password?.hasError}
                    errorName={error?.password?.name}
                    isPasswordType={true}
                    isPasswordVisible={isPasswordVisible}
                    onChange={(value) => {
                        setPassword(value);
                        setError({});
                    }}
                    onBlur={(e) => handleSetError(e.target.value)}
                    onClickInputButton={() => setPasswordVisible(!isPasswordVisible)}
                    $locale={$locale}
                />
                <UIButton
                    type="button"
                    disabled={disabled || passwordLockTime}
                    onClick={handleResetPassword}
                    color="transparent"
                    style={`link ${!passwordLockTime ? 'accent' : ''}`}
                >
                    {$locale.get('auth.login.button.sendCodeAgain')}
                    {passwordLockTime && (
                        <>
                            &nbsp;{$locale.get('auth.login.button.sendCodeAgainVia')}
                            <PasswordLock passwordLockTime={passwordLockTime} onStop={() => setPasswordLockTime(null)} />
                        </>
                    )}
                </UIButton>
                {phoneAuthAllowed &&
                    <div style={{ marginTop: '0.5rem' }}>
                        <UIButton
                            type="button"
                            color="transparent"
                            style="link accent"
                            disabled={disabled}
                            onClick={handleSendSMS}
                        >
                            {$locale.get('auth.login.button.sendSMS')}
                        </UIButton>
                    </div>
                }
            </div>
            <UIButton
                type="submit"
                size="large"
                color={password && !error.password ? 'primary' : 'not-active'}
                disabled={disabled}
            >
                {$locale.get('auth.login.submit')}
            </UIButton>
        </form>
    );
}
